import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import CapsuleTab from '@repo/ui/capsule-tab'
import { Tab } from '@repo/ui/capsule-tab'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { getAffectedStaffActions } from '../../../api/affected-staffs/get-affected-staff-actions'
import { getCenterActions } from '../../../api/centers/get-center-actions'
import CountryFlag from '../../../components/elements/country-flag'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import TransportPicto from '../../../components/elements/transport-picto'
import { Paths } from '../../../lib/constants/path'
import { AffectedStaffContext } from '../../../lib/contexts/affected-staff'
import useTranslate from '../../../lib/hooks/use-translate'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { canAffectedStaffAccessQuestionnaire } from '../../../lib/utils/functions/affected-staff-questionnaire.functions'

type StaffTab = Tab & { path: string }

const StaffPersonalSpaceJourneyDetails = () => {
  useParams()
  const [activeTab, setActiveTab] = useState(0)
  const { t } = useTranslate()
  const { navigate } = useNavigator()
  const { pathname } = useLocation()

  const { affectedStaff } = useContext(AffectedStaffContext)
  const center = affectedStaff?.staffRequest?.centers?.[0]

  const { data: centerActions } = useQuery({
    queryKey: ['center-actions', center?.id],
    queryFn: () => {
      if (center?.id) return getCenterActions(center.id)
    },
    enabled: !!center?.id
  })

  const { data: affectedStaffActions } = useQuery({
    queryKey: ['affected-staff-actions', affectedStaff?.id],
    queryFn: () => {
      if (affectedStaff?.id) return getAffectedStaffActions(affectedStaff.id)
    },
    enabled: !!affectedStaff?.id
  })

  const canViewActivitiesAndBudget =
    centerActions && centerActions.canViewCenterActivitiesAndBudget
  const canViewQuestionnaire = canAffectedStaffAccessQuestionnaire(affectedStaff)
  const canAccessDailyReport = !!affectedStaffActions?.canAccessDailyReport
  const canAccessEndOfStayReport = !!affectedStaffActions?.canAccessEndOfStayReport

  const tabs = useMemo(() => {
    const staffTabs: (StaffTab & { hidden?: boolean })[] = [
      {
        label: t('staff.personal.space.my.journeys.details.tab.home'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_HOME
      },
      {
        label: t('staff.personal.space.my.journeys.details.tab.guide'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_GUIDE
      },
      {
        label: t('staff.personal.space.my.journeys.details.trip.time'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_TRIP_INFORMATION
      },
      {
        label: t('staff.personal.space.my.journeys.details.students'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_STUDENTS
      },
      {
        label: t('staff.personal.space.my.journeys.details.activities.and.budget'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_ACTIVITIES_AND_BUDGET,
        disabled: !canViewActivitiesAndBudget
      },
      {
        label: t('staff.personal.space.my.journeys.details.daily.report'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_DAILY_REPORT,
        hidden: !canAccessDailyReport
      },
      {
        label: t('staff.personal.space.my.journeys.details.end.of.stay.report'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_END_OF_STAY_REPORT,
        hidden: !canAccessEndOfStayReport
      },
      {
        label: t('staff.personal.space.my.journeys.details.questionnaire'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_QUESTIONNAIRE,
        hidden: !canViewQuestionnaire
      },
      {
        label: t('staff.personal.space.my.journeys.details.expenses.note'),
        path: Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_EXPENSES_NOTE
      }
    ]

    return staffTabs.filter(({ hidden }) => !hidden)
  }, [
    canViewActivitiesAndBudget,
    canViewQuestionnaire,
    canAccessDailyReport,
    canAccessEndOfStayReport,
    t
  ])

  useEffect(() => {
    for (const [index, { path }] of Object.entries(tabs))
      if (pathname.includes(path)) {
        setActiveTab(+index)
        return
      }
  }, [pathname, tabs])

  if (!affectedStaff) return <LoadingScreen />

  return (
    <div className="flex flex-col gap-5 mt-5">
      <Box className="rounded-2xl p-5 flex flex-col gap-2">
        <InnerBox className="bg-white-hover py-2 px-3 flex flex-row gap-2 items-center mobile:flex-col-reverse mobile:items-start">
          <Typography variant="h4" color={tailwindColors.black.DEFAULT}>
            {`${StaffRequestProperties.getFormulaName(affectedStaff.staffRequest)} - ${affectedStaff.staffRequest.centers.flatMap((center) => CenterProperties.getCenterThemeName(center)).join(', ')} `}
          </Typography>
          <div className="flex items-center gap-2">
            {affectedStaff.staffRequest.country && (
              <CountryFlag
                code={affectedStaff.staffRequest.country?.code}
                possibleCountry={affectedStaff.staffRequest.country?.name}
              />
            )}
            {affectedStaff.trip?.transportMeans?.picto && (
              <TransportPicto picto={affectedStaff.trip?.transportMeans?.picto} />
            )}
          </div>
        </InnerBox>
        <div className="h-2" />
        <div className="sticky top-2 z-10">
          <CapsuleTab
            variant="secondary"
            activeTab={activeTab}
            onTabChange={(index) => {
              const path = tabs[index]?.path
              if (!path) return

              setActiveTab(index)
              // If the user already navigated to the tab, we don't want to navigate again
              if (pathname.includes(path)) return
              navigate(path)
            }}
            tabs={tabs}
          />
          <div className="h-4" />
          <Outlet />
        </div>
      </Box>
    </div>
  )
}

export default StaffPersonalSpaceJourneyDetails
